// /utils/reports/proval/rqFixedInterval.js

/**
 * RQ Fixed Interval Export
 * Source: segments (excluding exceptions)
 * Output: Start, Stop, Length, Left IRI, Right IRI, MRI
 */

function sanitizeFilename(str) {
  return str.replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 50);
}

function padColumn(value, width, align = 'right') {
  const str = String(value);
  if (align === 'left') {
    return str.padEnd(width);
  }
  return str.padStart(width);
}

export function generateRQFixedInterval(segments, options = {}) {
  const { format = 'txt' } = options;
  
  // Sort all segments to find true session start (including exceptions)
  const allSorted = [...segments].sort((a, b) => a.from_station_ft - b.from_station_ft);
  const sessionStart = allSorted[0]?.from_station_ft ?? 0;
  
  // Filter to non-exception segments only
  const validSegments = allSorted.filter(seg => !seg.exception);
  
  if (format === 'csv') {
    // CSV format - simple comma-delimited
    const headers = [
      'Start Distance (ft)',
      'Stop Distance (ft)',
      'Length (ft)',
      'Left IRI (in/mi)',
      'Right IRI (in/mi)',
      'MRI (in/mi)'
    ];
    
    const lines = [headers.join(',')];
    
    validSegments.forEach(seg => {
      const startDist = seg.from_station_ft - sessionStart;
      const stopDist = seg.to_station_ft - sessionStart;
      const length = seg.distance_ft ?? (stopDist - startDist);
      
      const row = [
        startDist.toFixed(1),
        stopDist.toFixed(1),
        length.toFixed(1),
        seg.iri_left_in_per_mi?.toFixed(2) ?? '',
        seg.iri_right_in_per_mi?.toFixed(2) ?? '',
        seg.iri_avg_in_per_mi?.toFixed(2) ?? ''
      ];
      lines.push(row.join(','));
    });
    
    return lines.join('\n');
  }
  
  // TXT format - formatted columns
  const COL_WIDTHS = {
    start: 14,
    stop: 14,
    length: 12,
    leftIri: 14,
    rightIri: 14,
    mri: 12
  };
  
  const lines = [];
  
  // Header row
  lines.push([
    padColumn('Start (ft)', COL_WIDTHS.start),
    padColumn('Stop (ft)', COL_WIDTHS.stop),
    padColumn('Length (ft)', COL_WIDTHS.length),
    padColumn('Left IRI', COL_WIDTHS.leftIri),
    padColumn('Right IRI', COL_WIDTHS.rightIri),
    padColumn('MRI', COL_WIDTHS.mri)
  ].join('  '));
  
  // Separator
  lines.push([
    '-'.repeat(COL_WIDTHS.start),
    '-'.repeat(COL_WIDTHS.stop),
    '-'.repeat(COL_WIDTHS.length),
    '-'.repeat(COL_WIDTHS.leftIri),
    '-'.repeat(COL_WIDTHS.rightIri),
    '-'.repeat(COL_WIDTHS.mri)
  ].join('  '));
  
  // Data rows
  validSegments.forEach(seg => {
    const startDist = seg.from_station_ft - sessionStart;
    const stopDist = seg.to_station_ft - sessionStart;
    const length = seg.distance_ft ?? (stopDist - startDist);
    
    lines.push([
      padColumn(startDist.toFixed(1), COL_WIDTHS.start),
      padColumn(stopDist.toFixed(1), COL_WIDTHS.stop),
      padColumn(length.toFixed(1), COL_WIDTHS.length),
      padColumn(seg.iri_left_in_per_mi?.toFixed(2) ?? '--', COL_WIDTHS.leftIri),
      padColumn(seg.iri_right_in_per_mi?.toFixed(2) ?? '--', COL_WIDTHS.rightIri),
      padColumn(seg.iri_avg_in_per_mi?.toFixed(2) ?? '--', COL_WIDTHS.mri)
    ].join('  '));
  });
  
  // Summary footer
  lines.push('');
  lines.push([
    '-'.repeat(COL_WIDTHS.start),
    '-'.repeat(COL_WIDTHS.stop),
    '-'.repeat(COL_WIDTHS.length),
    '-'.repeat(COL_WIDTHS.leftIri),
    '-'.repeat(COL_WIDTHS.rightIri),
    '-'.repeat(COL_WIDTHS.mri)
  ].join('  '));
  
  // Calculate totals/averages
  const totalLength = validSegments.reduce((sum, s) => sum + (s.distance_ft || 0), 0);
  const avgLeftIri = validSegments.reduce((sum, s) => sum + (s.iri_left_in_per_mi || 0), 0) / validSegments.length;
  const avgRightIri = validSegments.reduce((sum, s) => sum + (s.iri_right_in_per_mi || 0), 0) / validSegments.length;
  const avgMri = validSegments.reduce((sum, s) => sum + (s.iri_avg_in_per_mi || 0), 0) / validSegments.length;
  
  lines.push([
    padColumn('TOTAL:', COL_WIDTHS.start),
    padColumn('', COL_WIDTHS.stop),
    padColumn(totalLength.toFixed(1), COL_WIDTHS.length),
    padColumn(avgLeftIri.toFixed(2), COL_WIDTHS.leftIri),
    padColumn(avgRightIri.toFixed(2), COL_WIDTHS.rightIri),
    padColumn(avgMri.toFixed(2), COL_WIDTHS.mri)
  ].join('  '));
  
  lines.push(`\nSegments: ${validSegments.length} | Total Length: ${totalLength.toFixed(1)} ft (${(totalLength / 5280).toFixed(3)} mi)`);
  
  return lines.join('\n');
}

export function getRQFixedIntervalFilename(session, format = 'txt') {
  const name = sanitizeFilename(session?.name || 'Session');
  return `RQ_Fixed_Interval_${name}.${format}`;
}
